import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Bot, Send, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

const AgentChat = ({ agentName, hotelId, icon, color, description }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Storage key for conversation history
  const storageKey = `agent-chat-${hotelId}-${agentName}`;

  // Load conversation history from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setMessages(parsed);
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
    }
  }, [storageKey]);

  // Save conversation history to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(messages));
      } catch (error) {
        console.error('Failed to save conversation history:', error);
      }
    }
  }, [messages, storageKey]);

  const clearHistory = () => {
    setMessages([]);
    localStorage.removeItem(storageKey);
    toast.success(`${agentName} conversation cleared`);
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/blockchain/agents/${hotelId}/${agentName}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_prompt: input })
      });

      const data = await response.json();
      
      if (data.success && data.agent_run) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.agent_run.final_response || 'Task completed.',
          toolCalls: data.agent_run.tool_calls || []
        }]);
      } else {
        toast.error('Agent failed to respond');
      }
    } catch (error) {
      console.error('Agent error:', error);
      toast.error('Failed to contact agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[500px]">
      {/* Info Banner with Clear Button */}
      <div className="p-3 bg-muted/50 rounded-lg mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <p className="text-sm font-medium">{description}</p>
            {messages.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {messages.length} message{messages.length !== 1 ? 's' : ''} in history
              </p>
            )}
          </div>
        </div>
        {messages.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearHistory}
            className="flex-shrink-0"
            data-testid={`clear-${agentName}-history`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-3 p-3 border rounded-lg bg-muted/20">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <span className="text-4xl mb-3">{icon}</span>
            <p className="text-muted-foreground text-sm">Start a conversation with {agentName}</p>
            <p className="text-xs text-muted-foreground mt-1">Ask questions, request actions, or get insights</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-lg ${
                msg.role === 'user' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card border'
              }`}>
                <p className="text-sm">{msg.content}</p>
                {msg.toolCalls && msg.toolCalls.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/30 space-y-1">
                    {msg.toolCalls.map((call, i) => (
                      <Badge key={i} variant="secondary" className="text-xs mr-1">
                        {call.tool_name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-card border p-3 rounded-lg">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <Input
          placeholder={`Message ${agentName}...`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          disabled={loading}
          data-testid={`${agentName}-chat-input`}
        />
        <Button 
          onClick={sendMessage} 
          disabled={loading || !input.trim()}
          data-testid={`${agentName}-send-btn`}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const AgentChatSection = ({ hotelId }) => {
  if (!hotelId) return null;

  return (
    <Card data-testid="agent-chat-section">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          <CardTitle>AI Agent Assistants</CardTitle>
        </div>
        <CardDescription>
          Chat with your hotel's AI agents for real-time assistance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="bitsy" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="bitsy" data-testid="bitsy-tab">
              <span className="mr-2">🤖</span>
              Bitsy
            </TabsTrigger>
            <TabsTrigger value="marina" data-testid="marina-tab">
              <span className="mr-2">🌊</span>
              Marina
            </TabsTrigger>
            <TabsTrigger value="sage" data-testid="sage-tab">
              <span className="mr-2">🧠</span>
              Sage
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="bitsy" className="mt-4">
            <AgentChat 
              agentName="bitsy"
              hotelId={hotelId}
              icon="🤖"
              color="#8b5cf6"
              description="Guest concierge - handles requests, recommendations, and guest services"
            />
          </TabsContent>
          
          <TabsContent value="marina" className="mt-4">
            <AgentChat 
              agentName="marina"
              hotelId={hotelId}
              icon="🌊"
              color="#06b6d4"
              description="Booking specialist - manages reservations, modifications, and accommodations"
            />
          </TabsContent>
          
          <TabsContent value="sage" className="mt-4">
            <AgentChat 
              agentName="sage"
              hotelId={hotelId}
              icon="🧠"
              color="#10b981"
              description="Operations expert - provides insights, optimizations, and strategic guidance"
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AgentChatSection;
